import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  MessageSquareIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "lucide-react";
import { useState } from "react";
import type { ReviewComment } from "@/types/assessment";
import { CommentBadge } from "./comment-badge";

interface CommentsPanelProps {
  comments: ReviewComment[];
  onEdit: (comment: ReviewComment) => void;
  onDelete: (commentId: string) => void;
  onScrollToLine?: (lineNumber: number) => void;
  disabled?: boolean;
}

export function CommentsPanel({
  comments,
  onEdit,
  onDelete,
  onScrollToLine,
  disabled = false,
}: CommentsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (comments.length === 0) {
    return (
      <div className="p-4 rounded-lg border border-dashed bg-muted/30 text-center">
        <MessageSquareIcon className="size-8 mx-auto mb-2 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">
          No comments yet. Click the{" "}
          <span className="font-mono bg-muted px-1 rounded">+</span> button on
          any line to add a review comment.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <MessageSquareIcon className="size-4 text-muted-foreground" />
          <span className="font-medium">Your Comments</span>
          <span className="text-sm text-muted-foreground">
            ({comments.length})
          </span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="size-4 text-muted-foreground" />
        ) : (
          <ChevronDownIcon className="size-4 text-muted-foreground" />
        )}
      </button>

      {/* Comments list */}
      {isExpanded && (
        <div className="divide-y">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onEdit={() => onEdit(comment)}
              onDelete={() => onDelete(comment.id)}
              onScrollToLine={onScrollToLine}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CommentItemProps {
  comment: ReviewComment;
  onEdit: () => void;
  onDelete: () => void;
  onScrollToLine?: (lineNumber: number) => void;
  disabled?: boolean;
}

function CommentItem({
  comment,
  onEdit,
  onDelete,
  onScrollToLine,
  disabled = false,
}: CommentItemProps) {
  const lineRange =
    comment.end_line && comment.end_line !== comment.start_line
      ? `L${comment.start_line}-${comment.end_line}`
      : `L${comment.start_line}`;

  return (
    <div className="p-4 hover:bg-muted/30 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 space-y-2">
          {/* Line number and category */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onScrollToLine?.(comment.start_line)}
              className="font-mono text-sm text-primary hover:underline"
            >
              {lineRange}
            </button>
            {comment.filename && (
              <span className="text-sm text-muted-foreground">
                in {comment.filename}
              </span>
            )}
            {comment.category && (
              <CommentBadge category={comment.category} size="sm" />
            )}
          </div>

          {/* Description */}
          <p className="text-sm">{comment.description}</p>

          {/* Suggestion */}
          {comment.suggestion && (
            <div className="flex items-start gap-2 p-2 rounded bg-muted/50 text-sm">
              <span className="text-muted-foreground shrink-0">💡</span>
              <span className="text-muted-foreground">{comment.suggestion}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        {!disabled && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={onEdit}
            >
              <PencilIcon className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <TrashIcon className="size-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
